// HRV PSD 분석 함수들

import { BCGData, PSDData } from './types';

/**
 * HRV PSD 분석
 * 실제 구현에서는 FFT를 사용해야 하지만, 여기서는 간단한 구현을 제공
 */
export function calculateHRVPSD(sigData: BCGData, fs: number, peaks: number[]): PSDData {
  if (!peaks || peaks.length < 10) {
    return {
      TP: NaN,
      VLF: NaN,
      LF: NaN,
      HF: NaN,
      LFnu: NaN,
      HFnu: NaN,
      LF_HF: NaN
    };
  }

  // RR 간격 계산 (ms)
  const rrMs = peaks.slice(1).map((peak, i) => (peak - peaks[i]) / fs * 1000);
  
  // 유효한 RR 간격 필터링 (300-2000ms)
  const validIndices = rrMs.map((rr, i) => rr >= 300 && rr <= 2000 ? i : -1).filter(i => i >= 0);
  const validRR = validIndices.map(i => rrMs[i] / 1000); // 초 단위로 변환
  
  if (validRR.length < 8) {
    return {
      TP: NaN,
      VLF: NaN,
      LF: NaN,
      HF: NaN,
      LFnu: NaN,
      HFnu: NaN,
      LF_HF: NaN
    };
  }

  // 시간 축 생성
  const timePoints = validIndices.map(i => sigData.t[peaks[i + 1]].getTime() / 1000);
  
  if (timePoints.length === 0 || (timePoints[timePoints.length - 1] - timePoints[0]) < 120) {
    return {
      TP: NaN,
      VLF: NaN,
      LF: NaN,
      HF: NaN,
      LFnu: NaN,
      HFnu: NaN,
      LF_HF: NaN
    };
  }

  // 4Hz로 리샘플링을 위한 그리드 생성
  const startTime = timePoints[0];
  const endTime = timePoints[timePoints.length - 1];
  const resampleRate = 4.0; // 4Hz
  const gridSize = Math.floor((endTime - startTime) * resampleRate);
  const grid = Array.from({ length: gridSize }, (_, i) => startTime + i / resampleRate);

  // 선형 보간
  const mean = validRR.reduce((sum, val) => sum + val, 0) / validRR.length;
  const interpolatedRR = grid.map(t => {
    // 간단한 선형 보간
    for (let i = 0; i < timePoints.length - 1; i++) {
      if (t >= timePoints[i] && t <= timePoints[i + 1]) {
        const ratio = (t - timePoints[i]) / (timePoints[i + 1] - timePoints[i]);
        return validRR[i] + ratio * (validRR[i + 1] - validRR[i]) - mean;
      }
    }
    return 0;
  });

  // 간단한 Welch 방법 구현 (실제로는 FFT 라이브러리 사용 권장)
  const psd = calculateSimplePSD(interpolatedRR, resampleRate);
  
  // 주파수 대역별 전력 계산
  const TP = integrateBand(psd.frequencies, psd.power, 0.0033, 0.4);
  const VLF = integrateBand(psd.frequencies, psd.power, 0.0033, 0.04);
  const LF = integrateBand(psd.frequencies, psd.power, 0.04, 0.15);
  const HF = integrateBand(psd.frequencies, psd.power, 0.15, 0.4);

  // 정규화된 단위 계산
  const denominator = TP - VLF;
  const LFnu = denominator > 0 ? (LF / denominator) * 100 : NaN;
  const HFnu = denominator > 0 ? (HF / denominator) * 100 : NaN;
  const LF_HF = HF > 0 ? LF / HF : NaN;

  return {
    TP,
    VLF,
    LF,
    HF,
    LFnu,
    HFnu,
    LF_HF
  };
}

/**
 * 간단한 PSD 계산 (실제로는 FFT 사용 권장)
 */
function calculateSimplePSD(signal: number[], fs: number): { frequencies: number[]; power: number[] } {
  const N = signal.length;
  const frequencies: number[] = [];
  const power: number[] = [];

  // 주파수 해상도
  const df = fs / N;
  
  // 0.4Hz까지의 주파수만 계산
  const maxFreq = 0.4;
  const maxBin = Math.floor(maxFreq / df);

  for (let k = 0; k <= maxBin; k++) {
    const freq = k * df;
    frequencies.push(freq);
    
    // 간단한 DFT 계산 (실제로는 FFT 사용)
    let real = 0;
    let imag = 0;
    
    for (let n = 0; n < N; n++) {
      const angle = -2 * Math.PI * k * n / N;
      real += signal[n] * Math.cos(angle);
      imag += signal[n] * Math.sin(angle);
    }
    
    const magnitude = Math.sqrt(real * real + imag * imag) / N;
    power.push(magnitude * magnitude);
  }

  return { frequencies, power };
}

/**
 * 주파수 대역별 전력 적분
 */
function integrateBand(frequencies: number[], power: number[], lowFreq: number, highFreq: number): number {
  let integral = 0;
  
  for (let i = 0; i < frequencies.length - 1; i++) {
    const f1 = frequencies[i];
    const f2 = frequencies[i + 1];
    
    if (f1 >= lowFreq && f2 <= highFreq) {
      // 사다리꼴 적분
      integral += (power[i] + power[i + 1]) * (f2 - f1) / 2;
    }
  }
  
  return integral;
}

